import { creditsSettledAfterPhone, type DailyCeilingSnapshot } from './credits-refresh-after-phone';

/**
 * "Verify with WhatsApp" (2026-11-02). The person sends Symphony ONE prefilled WhatsApp message carrying a code;
 * the backend (VMR RecipientService.Start/Complete WhatsappPhoneVerification, digest-engine reading the code off
 * the inbound message) verifies the SENDER's number. Two modes share it: `credits`, the daily-limit offer, where the
 * daily limit rises to `verifyPhoneLimit`; and `connect` (2026-11-03), the first step of every "connect your phone"
 * ask while no phone is on file, where the recipient gains the number.
 *
 * The contract web, iOS and Android all implement is `native-apps/shared/whatsapp-phone-verify/contract.json`
 * (link format + test vector, polling rule, states, strings, BI names); `test/whatsapp-phone-verify.test.ts`
 * reads that JSON, so drift fails the build.
 *
 * Pure and app-graph-free (its one import is the import-free `credits-refresh-after-phone`, whose settle rule
 * it reuses rather than copies), so every rule here is unit-tested in node.
 */

/** The number the message goes to — Symphony's WhatsApp sender. */
export const WHATSAPP_VERIFY_NUMBER = '15188556881';

/** How often the dialog re-reads the credits while it waits for the message. */
export const WHATSAPP_VERIFY_POLL_MS = 4_000;

/** How long the done state shows before the dialog closes itself. */
export const WHATSAPP_VERIFY_DONE_CLOSE_MS = 1_500;

/** The token the backend reads off the message: `[PV-<code>]`. */
export function whatsappVerifyCodeToken(code: string): string {
  return `[PV-${code}]`;
}

/**
 * The wa.me link for a code and the (localized) prefill TEMPLATE, whose `{code}` becomes `[PV-<code>]`. A
 * template that lost its `{code}` (a bad translation) still carries the token, appended — a message without
 * it can verify nothing.
 */
export function whatsappVerifyLink(code: string, prefillTemplate: string): string {
  const token = whatsappVerifyCodeToken(code);
  const text = prefillTemplate.includes('{code}') ? prefillTemplate.split('{code}').join(token) : `${prefillTemplate} ${token}`.trim();
  return `https://wa.me/${WHATSAPP_VERIFY_NUMBER}?text=${encodeURIComponent(text)}`;
}

/** A minted code, as the start call answers it. */
export interface WhatsappVerifyTicket {
  code: string;
  /** Epoch ms. */
  expiresAtMs: number;
}

/** base64url, as the backend mints it (40 characters today; any sane length is accepted). */
const CODE_RE = /^[A-Za-z0-9_-]{8,128}$/;

/**
 * The start call's answer → a ticket, or null for anything that is not `{ok:true, code, expiresAt}` with a
 * well-formed code and a parseable time. Null is the error state: never open WhatsApp without a code.
 */
export function parseWhatsappVerifyStart(body: unknown): WhatsappVerifyTicket | null {
  if (!body || typeof body !== 'object') return null;
  const { ok, code, expiresAt } = body as { ok?: unknown; code?: unknown; expiresAt?: unknown };
  if (ok !== true || typeof code !== 'string' || !CODE_RE.test(code) || typeof expiresAt !== 'string') return null;
  const expiresAtMs = Date.parse(expiresAt);
  return Number.isNaN(expiresAtMs) ? null : { code, expiresAtMs };
}

export type WhatsappVerifyState =
  | { kind: 'loading' }
  | { kind: 'ready'; ticket: WhatsappVerifyTicket }
  | { kind: 'waiting'; ticket: WhatsappVerifyTicket }
  | { kind: 'done' }
  | { kind: 'expired' }
  | { kind: 'error' };

export type WhatsappVerifyEvent =
  /** A (re)mint started — on open, and on Open WhatsApp from expired/error. */
  | { type: 'mint' }
  /** The start call answered; `body` is its JSON (or null when the read failed). */
  | { type: 'minted'; body: unknown }
  /** The person tapped Open WhatsApp with a live code. */
  | { type: 'opened' }
  /**
   * A read landed (poll, focus) or a clock tick: is it done, or expired? `settled` is the mode's done rule
   * applied to that read (`whatsappVerifySettled` for credits, `whatsappConnectSettled` for connect).
   */
  | { type: 'check'; nowMs: number; settled: boolean };

export const WHATSAPP_VERIFY_INITIAL: WhatsappVerifyState = { kind: 'loading' };

export type WhatsappVerifyMode = 'credits' | 'connect';

/** What a mode changes: its title, body and done copy, and its BI names (contract `modes`). */
export interface WhatsappVerifyModeCopy {
  titleKey: string;
  bodyKey: string;
  doneKey: string;
  bi: { sheetSeen: string; clicks: { open: string; useNumber: string } };
}

const MODE_COPY: Record<WhatsappVerifyMode, WhatsappVerifyModeCopy> = {
  credits: {
    titleKey: 'credits.verifyWhatsapp.title',
    bodyKey: 'credits.verifyWhatsapp.body',
    doneKey: 'credits.verifyWhatsapp.done',
    bi: { sheetSeen: 'verify-phone-whatsapp', clicks: { open: 'verify_phone_whatsapp_open', useNumber: 'verify_phone_whatsapp_use_number' } },
  },
  connect: {
    titleKey: 'phone.connectWhatsapp.title',
    bodyKey: 'phone.connectWhatsapp.body',
    doneKey: 'phone.connectWhatsapp.done',
    bi: { sheetSeen: 'connect-phone-whatsapp', clicks: { open: 'connect_phone_whatsapp_open', useNumber: 'connect_phone_whatsapp_use_number' } },
  },
};

export function whatsappVerifyModeCopy(mode: WhatsappVerifyMode): WhatsappVerifyModeCopy {
  return MODE_COPY[mode];
}

/**
 * Connect mode's done rule: a re-read of the recipient carries a phone. The sheet opens only while the recipient
 * has none (`recipientNeedsPhone`), so any phone on a later read is the one the message just verified.
 */
export function whatsappConnectSettled(recipient: { phone?: string | null } | null | undefined): boolean {
  return !!recipient?.phone;
}

/**
 * Done when the credits read has moved since the dialog opened — the SAME rule as the code path's
 * re-read (`creditsSettledAfterPhone`: the limit changed, or `verifyPhoneLimit` is gone). Unlike that
 * path, "nothing to compare against" is NOT done here: the dialog opened on an offer, so a missing
 * before-snapshot means the read had not landed, not that the wall was never up.
 */
export function whatsappVerifySettled(before: DailyCeilingSnapshot | null, after: DailyCeilingSnapshot | null): boolean {
  return before !== null && creditsSettledAfterPhone(before, after);
}

export function whatsappVerifyReducer(state: WhatsappVerifyState, event: WhatsappVerifyEvent): WhatsappVerifyState {
  switch (event.type) {
    case 'mint':
      return state.kind === 'done' ? state : { kind: 'loading' };
    case 'minted': {
      if (state.kind !== 'loading') return state;
      const ticket = parseWhatsappVerifyStart(event.body);
      return ticket ? { kind: 'ready', ticket } : { kind: 'error' };
    }
    case 'opened':
      return state.kind === 'ready' ? { kind: 'waiting', ticket: state.ticket } : state;
    case 'check':
      // Ready polls too: on a computer the person may scan the QR and never press Open WhatsApp.
      if (state.kind !== 'ready' && state.kind !== 'waiting') return state;
      if (event.settled) return { kind: 'done' };
      return event.nowMs >= state.ticket.expiresAtMs ? { kind: 'expired' } : state;
    default:
      return state;
  }
}

/** Whether the dialog should be polling the credits in this state. */
export function whatsappVerifyPolls(state: WhatsappVerifyState): boolean {
  return state.kind === 'ready' || state.kind === 'waiting';
}

/** The link for a state that has a live code, else null (Open WhatsApp then mints a fresh one). */
export function whatsappVerifyStateLink(state: WhatsappVerifyState, prefillTemplate: string): string | null {
  return state.kind === 'ready' || state.kind === 'waiting' ? whatsappVerifyLink(state.ticket.code, prefillTemplate) : null;
}
